import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PageHeader, StatCard, TableWrap, Th, Td, Loading, EmptyState, Badge, exportCSV } from "../components/shared";
import { formatINR, formatDate, todayISO } from "../lib/format";
import { DATE_RANGES, TXN_TYPE_META } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Printer, Download } from "lucide-react";

function RangeBar({ range, setRange, custom, setCustom }) {
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-3 mb-5 flex flex-wrap gap-1.5 items-center no-print">
      {DATE_RANGES.map((r) => (
        <button key={r.key} onClick={() => setRange(r.key)} data-testid={`rr-${r.key}`}
          className={`text-xs font-medium px-3 py-1.5 rounded-lg ${range === r.key ? "bg-[#0F2942] text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}>{r.label}</button>
      ))}
      {range === "custom" && (
        <div className="flex gap-2 ml-2">
          <Input type="date" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} className="h-8 w-36" />
          <Input type="date" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} className="h-8 w-36" />
        </div>
      )}
    </div>
  );
}

function usePeriod(defaultRange = "this_month") {
  const [range, setRange] = useState(defaultRange);
  const [custom, setCustom] = useState({ from: todayISO(), to: todayISO() });
  const qs = () => range === "custom" ? `range=custom&date_from=${custom.from}&date_to=${custom.to}` : `range=${range}`;
  return { range, setRange, custom, setCustom, qs };
}

const Delta = ({ cur, prev }) => {
  if (!prev) return null;
  const pct = ((cur - prev) / Math.abs(prev)) * 100;
  const up = pct >= 0;
  return <span className={`text-[11px] ${up ? "text-emerald-600" : "text-rose-600"}`}>{up ? "▲" : "▼"} {Math.abs(pct).toFixed(1)}% vs prev</span>;
};

export function PeriodReport() {
  const p = usePeriod();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/reports/period?${p.qs()}`); setData(data); }
    finally { setLoading(false); }
  }, [p.range, p.custom]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <PageHeader title="Period Report" subtitle="Weekly / monthly summary with previous-period comparison"
        actions={<Button size="sm" variant="outline" onClick={() => window.print()} className="gap-1.5 no-print"><Printer className="h-4 w-4" /> Print</Button>} />
      <RangeBar {...p} />
      {loading || !data ? <Loading /> : (
        <div className="print-area">
          <div className="text-sm text-slate-500 mb-3">{formatDate(data.from)} to {formatDate(data.to)}</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <StatCard label="Recognized Income" value={data.agg.income} accent="blue" sub={<Delta cur={data.agg.income} prev={data.prev.income} />} />
            <StatCard label="Expenses" value={data.agg.expense} accent="amber" sub={<Delta cur={data.agg.expense} prev={data.prev.expense} />} />
            <StatCard label="Net Profit" value={data.agg.net_profit} accent={data.agg.net_profit >= 0 ? "emerald" : "rose"} sub={<Delta cur={data.agg.net_profit} prev={data.prev.net_profit} />} />
            <StatCard label="Money Received" value={data.agg.money_in} accent="emerald" />
            <StatCard label="Candidate Advances" value={data.agg.candidate_advance} accent="violet" />
            <StatCard label="Advances Paid" value={data.agg.advances_paid} accent="violet" />
            <StatCard label="Refunds" value={data.agg.refunds} accent="rose" />
            <StatCard label="Transfers" value={data.agg.transfers} accent="slate" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div><h3 className="font-heading font-semibold text-sm mb-2">Top Income Categories</h3>
              <TableWrap><thead><tr><Th>Category</Th><Th className="text-right">Amount</Th></tr></thead>
                <tbody>{data.income_by_category.length ? data.income_by_category.map((c) => <tr key={c.id}><Td>{c.name}</Td><Td className="text-right font-mono">{formatINR(c.amount)}</Td></tr>) : <tr><Td colSpan={2}>No income</Td></tr>}</tbody></TableWrap>
            </div>
            <div><h3 className="font-heading font-semibold text-sm mb-2">Top Expense Categories</h3>
              <TableWrap><thead><tr><Th>Category</Th><Th className="text-right">Amount</Th></tr></thead>
                <tbody>{data.expense_by_category.length ? data.expense_by_category.map((c) => <tr key={c.id}><Td>{c.name}</Td><Td className="text-right font-mono">{formatINR(c.amount)}</Td></tr>) : <tr><Td colSpan={2}>No expenses</Td></tr>}</tbody></TableWrap>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function DailyReport() {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/reports/daily?date=${date}`); setData(data); }
    finally { setLoading(false); }
  }, [date]);
  useEffect(() => { load(); }, [load]);
  const cashClosing = data?.accounts.filter((a) => ["Cash", "Petty Cash"].includes(a.type)).reduce((s, a) => s + a.balance, 0) || 0;
  const bankClosing = data?.accounts.filter((a) => !["Cash", "Petty Cash"].includes(a.type)).reduce((s, a) => s + a.balance, 0) || 0;
  return (
    <div>
      <PageHeader title="Daily Report" subtitle="End-of-day financial summary"
        actions={<div className="flex gap-2 no-print"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-40" data-testid="daily-date" />
          <Button size="sm" variant="outline" onClick={() => window.print()} className="gap-1.5"><Printer className="h-4 w-4" /> Print</Button></div>} />
      {loading || !data ? <Loading /> : (
        <div className="print-area">
          <div className="text-sm text-slate-500 mb-3">Report for {formatDate(data.date)}</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <StatCard label="Total Received" value={data.agg.money_in} accent="emerald" />
            <StatCard label="Recognized Income" value={data.agg.income} accent="blue" />
            <StatCard label="Candidate Advances" value={data.agg.candidate_advance} accent="violet" />
            <StatCard label="Total Expenses" value={data.agg.expense} accent="amber" />
            <StatCard label="Agency Payments" value={data.agg.agency_payments} accent="rose" />
            <StatCard label="Refunds" value={data.agg.refunds} accent="rose" />
            <StatCard label="Cash Closing" value={cashClosing} accent="emerald" />
            <StatCard label="Bank Closing" value={bankClosing} accent="blue" />
          </div>
          <h3 className="font-heading font-semibold text-sm mb-2">Transactions ({data.transactions.length})</h3>
          {data.transactions.length === 0 ? <EmptyState title="No transactions on this day" /> : (
            <TableWrap>
              <thead><tr><Th>Txn</Th><Th>Type</Th><Th>Party</Th><Th>Description</Th><Th className="text-right">In</Th><Th className="text-right">Out</Th></tr></thead>
              <tbody>{data.transactions.map((t) => (
                <tr key={t.id}><Td className="font-mono">{t.txn_number}</Td>
                  <Td><Badge cls={TXN_TYPE_META[t.type]?.cls}>{TXN_TYPE_META[t.type]?.label || t.type}</Badge></Td>
                  <Td>{t.candidate_name || t.payee_name || "-"}</Td><Td>{t.description || "-"}</Td>
                  <Td className="text-right font-mono text-emerald-600">{t.money_in ? formatINR(t.money_in) : "-"}</Td>
                  <Td className="text-right font-mono text-rose-600">{(t.money_out || (t.type === "transfer" ? t.amount : 0)) ? formatINR(t.money_out || t.amount) : "-"}</Td></tr>
              ))}</tbody>
            </TableWrap>
          )}
        </div>
      )}
    </div>
  );
}

export function PnLReport() {
  const p = usePeriod("fy");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/reports/pnl?${p.qs()}`); setData(data); }
    finally { setLoading(false); }
  }, [p.range, p.custom]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <PageHeader title="Profit & Loss" subtitle="Based on recognized income & actual expenses (excludes advances & transfers)"
        actions={<Button size="sm" variant="outline" onClick={() => window.print()} className="gap-1.5 no-print"><Printer className="h-4 w-4" /> Print</Button>} />
      <RangeBar {...p} />
      {loading || !data ? <Loading /> : (
        <div className="print-area max-w-3xl">
          <div className="text-sm text-slate-500 mb-3">{formatDate(data.from)} to {formatDate(data.to)}</div>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm">
            <h3 className="font-heading font-bold text-blue-700 mb-2">Income</h3>
            {data.income.length ? data.income.map((c) => (
              <div key={c.id} className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800 text-sm"><span>{c.name}</span><span className="font-mono">{formatINR(c.amount)}</span></div>
            )) : <p className="text-sm text-slate-400 py-2">No recognized income</p>}
            <div className="flex justify-between py-2 font-bold text-blue-700"><span>Total Revenue</span><span className="font-mono">{formatINR(data.total_income)}</span></div>
            <h3 className="font-heading font-bold text-amber-700 mb-2 mt-5">Expenses</h3>
            {data.expense.length ? data.expense.map((c) => (
              <div key={c.id} className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800 text-sm"><span>{c.name}</span><span className="font-mono">{formatINR(c.amount)}</span></div>
            )) : <p className="text-sm text-slate-400 py-2">No expenses</p>}
            <div className="flex justify-between py-2 font-bold text-amber-700"><span>Total Expenses</span><span className="font-mono">{formatINR(data.total_expense)}</span></div>
            <div className={`flex justify-between py-3 mt-3 border-t-2 border-slate-800 text-lg font-extrabold ${data.net_profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
              <span>Net {data.net_profit >= 0 ? "Profit" : "Loss"}</span><span className="font-mono" data-testid="pnl-net">{formatINR(data.net_profit)}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CashFlowReport() {
  const p = usePeriod();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/reports/cashflow?${p.qs()}`); setData(data); }
    finally { setLoading(false); }
  }, [p.range, p.custom]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <PageHeader title="Cash Flow" subtitle="Actual money movement (differs from P&L: advances are cash but not income/expense)" />
      <RangeBar {...p} />
      {loading || !data ? <Loading /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-4xl">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm">
            <h3 className="font-heading font-bold text-emerald-600 mb-3">Cash / Bank Inflows</h3>
            <div className="flex justify-between py-1.5 text-sm border-b border-slate-100"><span>Candidate Advances</span><span className="font-mono">{formatINR(data.inflows.candidate_advance)}</span></div>
            <div className="flex justify-between py-1.5 text-sm border-b border-slate-100"><span>Other Receipts</span><span className="font-mono">{formatINR(data.inflows.recognized_receipts)}</span></div>
            <div className="flex justify-between py-2 font-bold text-emerald-600"><span>Total Inflow</span><span className="font-mono">{formatINR(data.inflows.total)}</span></div>
          </div>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm">
            <h3 className="font-heading font-bold text-rose-600 mb-3">Cash / Bank Outflows</h3>
            <div className="flex justify-between py-1.5 text-sm border-b border-slate-100"><span>Expenses</span><span className="font-mono">{formatINR(data.outflows.expense)}</span></div>
            <div className="flex justify-between py-1.5 text-sm border-b border-slate-100"><span>Advances Paid</span><span className="font-mono">{formatINR(data.outflows.advances_paid)}</span></div>
            <div className="flex justify-between py-1.5 text-sm border-b border-slate-100"><span>Refunds</span><span className="font-mono">{formatINR(data.outflows.refunds)}</span></div>
            <div className="flex justify-between py-2 font-bold text-rose-600"><span>Total Outflow</span><span className="font-mono">{formatINR(data.outflows.total)}</span></div>
          </div>
          <div className="lg:col-span-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm flex flex-wrap gap-8">
            <div><div className="text-xs text-slate-400 uppercase">Internal Transfers</div><div className="font-mono text-xl font-bold">{formatINR(data.transfers)}</div></div>
            <div><div className="text-xs text-slate-400 uppercase">Net Cash Movement</div><div className={`font-mono text-xl font-bold ${data.net_movement >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{formatINR(data.net_movement)}</div></div>
          </div>
        </div>
      )}
    </div>
  );
}

export function WeeklyReport() {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/reports/weekly?date=${date}`); setData(data); }
    finally { setLoading(false); }
  }, [date]);
  useEffect(() => { load(); }, [load]);
  const shift = (days) => { const d = new Date(date + "T00:00:00"); d.setDate(d.getDate() + days); setDate(d.toISOString().slice(0, 10)); };
  return (
    <div>
      <PageHeader title="Weekly Report" subtitle="Monday–Sunday summary with previous-week comparison"
        actions={<div className="flex gap-2 no-print">
          <Button size="sm" variant="outline" onClick={() => shift(-7)} data-testid="week-prev">◀ Prev</Button>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-40" data-testid="week-date" />
          <Button size="sm" variant="outline" onClick={() => shift(7)} data-testid="week-next">Next ▶</Button>
          <Button size="sm" variant="outline" onClick={() => window.print()} className="gap-1.5"><Printer className="h-4 w-4" /> Print</Button>
        </div>} />
      {loading || !data ? <Loading /> : (
        <div className="print-area">
          <div className="text-sm text-slate-500 mb-3">Week: {formatDate(data.from)} – {formatDate(data.to)} · compared with {formatDate(data.prev_from)} – {formatDate(data.prev_to)}</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <StatCard label="Total Collections" value={data.agg.money_in} accent="emerald" sub={<Delta cur={data.agg.money_in} prev={data.prev.money_in} />} />
            <StatCard label="Recognized Income" value={data.agg.income} accent="blue" sub={<Delta cur={data.agg.income} prev={data.prev.income} />} />
            <StatCard label="Total Expenses" value={data.agg.expense} accent="amber" sub={<Delta cur={data.agg.expense} prev={data.prev.expense} />} />
            <StatCard label="Net Profit" value={data.agg.net_profit} accent={data.agg.net_profit >= 0 ? "emerald" : "rose"} sub={<Delta cur={data.agg.net_profit} prev={data.prev.net_profit} />} />
            <StatCard label="Candidate Advances" value={data.agg.candidate_advance} accent="violet" />
            <StatCard label="Advances Paid" value={data.agg.advances_paid} accent="violet" />
            <StatCard label="Refunds" value={data.agg.refunds} accent="rose" />
            <StatCard label="Candidate Payments" value={data.candidate_payments} accent="slate" isCurrency={false} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <div><h3 className="font-heading font-semibold text-sm mb-2">Top Income Categories</h3>
              <TableWrap><thead><tr><Th>Category</Th><Th className="text-right">Amount</Th></tr></thead>
                <tbody>{data.income_by_category.length ? data.income_by_category.map((c) => <tr key={c.id}><Td>{c.name}</Td><Td className="text-right font-mono">{formatINR(c.amount)}</Td></tr>) : <tr><Td colSpan={2}>No income</Td></tr>}</tbody></TableWrap></div>
            <div><h3 className="font-heading font-semibold text-sm mb-2">Top Expense Categories</h3>
              <TableWrap><thead><tr><Th>Category</Th><Th className="text-right">Amount</Th></tr></thead>
                <tbody>{data.expense_by_category.length ? data.expense_by_category.map((c) => <tr key={c.id}><Td>{c.name}</Td><Td className="text-right font-mono">{formatINR(c.amount)}</Td></tr>) : <tr><Td colSpan={2}>No expenses</Td></tr>}</tbody></TableWrap></div>
          </div>
          <h3 className="font-heading font-semibold text-sm mb-2">Account-wise Movement</h3>
          {data.account_movement.length ? (
            <TableWrap><thead><tr><Th>Account</Th><Th className="text-right">Money In</Th><Th className="text-right">Money Out</Th><Th className="text-right">Net</Th></tr></thead>
              <tbody>{data.account_movement.map((a) => (<tr key={a.name}><Td className="font-medium">{a.name}</Td>
                <Td className="text-right font-mono text-emerald-600">{formatINR(a.in)}</Td><Td className="text-right font-mono text-rose-600">{formatINR(a.out)}</Td>
                <Td className="text-right font-mono font-semibold">{formatINR(a.in - a.out)}</Td></tr>))}</tbody></TableWrap>
          ) : <EmptyState title="No account movement this week" />}
        </div>
      )}
    </div>
  );
}

export function MonthlyReport() {
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/reports/monthly?month=${month}`); setData(data); }
    finally { setLoading(false); }
  }, [month]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <PageHeader title="Monthly Report" subtitle="Full-month summary with month-over-month comparison"
        actions={<div className="flex gap-2 no-print">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-44" data-testid="month-picker" />
          <Button size="sm" variant="outline" onClick={() => window.print()} className="gap-1.5"><Printer className="h-4 w-4" /> Print</Button>
        </div>} />
      {loading || !data ? <Loading /> : (
        <div className="print-area">
          <div className="text-sm text-slate-500 mb-3">{data.month_label} · compared with {data.prev_label}</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <StatCard label="Gross Collections" value={data.agg.money_in} accent="emerald" sub={<Delta cur={data.agg.money_in} prev={data.prev.money_in} />} />
            <StatCard label="Recognized Income" value={data.agg.income} accent="blue" sub={<Delta cur={data.agg.income} prev={data.prev.income} />} />
            <StatCard label="Total Expenses" value={data.agg.expense} accent="amber" sub={<Delta cur={data.agg.expense} prev={data.prev.expense} />} />
            <StatCard label="Net Profit" value={data.agg.net_profit} accent={data.agg.net_profit >= 0 ? "emerald" : "rose"} sub={<Delta cur={data.agg.net_profit} prev={data.prev.net_profit} />} />
            <StatCard label="Candidate Advances" value={data.agg.candidate_advance} accent="violet" />
            <StatCard label="Advances Paid" value={data.agg.advances_paid} accent="violet" />
            <StatCard label="Refunds" value={data.agg.refunds} accent="rose" />
            <StatCard label="Candidate Payments" value={data.candidate_payments} accent="slate" isCurrency={false} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <div><h3 className="font-heading font-semibold text-sm mb-2">Income Categories</h3>
              <TableWrap><thead><tr><Th>Category</Th><Th className="text-right">Amount</Th></tr></thead>
                <tbody>{data.income_by_category.length ? data.income_by_category.map((c) => <tr key={c.id}><Td>{c.name}</Td><Td className="text-right font-mono">{formatINR(c.amount)}</Td></tr>) : <tr><Td colSpan={2}>No income</Td></tr>}</tbody></TableWrap></div>
            <div><h3 className="font-heading font-semibold text-sm mb-2">Expense Categories</h3>
              <TableWrap><thead><tr><Th>Category</Th><Th className="text-right">Amount</Th></tr></thead>
                <tbody>{data.expense_by_category.length ? data.expense_by_category.map((c) => <tr key={c.id}><Td>{c.name}</Td><Td className="text-right font-mono">{formatINR(c.amount)}</Td></tr>) : <tr><Td colSpan={2}>No expenses</Td></tr>}</tbody></TableWrap></div>
          </div>
          <h3 className="font-heading font-semibold text-sm mb-2">Account-wise Movement</h3>
          {data.account_movement.length ? (
            <TableWrap><thead><tr><Th>Account</Th><Th className="text-right">Money In</Th><Th className="text-right">Money Out</Th><Th className="text-right">Net</Th></tr></thead>
              <tbody>{data.account_movement.map((a) => (<tr key={a.name}><Td className="font-medium">{a.name}</Td>
                <Td className="text-right font-mono text-emerald-600">{formatINR(a.in)}</Td><Td className="text-right font-mono text-rose-600">{formatINR(a.out)}</Td>
                <Td className="text-right font-mono font-semibold">{formatINR(a.in - a.out)}</Td></tr>))}</tbody></TableWrap>
          ) : <EmptyState title="No account movement this month" />}
        </div>
      )}
    </div>
  );
}

export function OutstandingReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/reports/outstanding").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);
  if (loading || !data) return <Loading />;
  return (
    <div>
      <PageHeader title="Outstanding Report" subtitle="Candidate receivables & agency pending advances" />
      <h3 className="font-heading font-semibold text-sm mb-2">Candidate Outstanding</h3>
      {data.candidates.length === 0 ? <EmptyState title="No candidate outstanding" /> : (
        <TableWrap>
          <thead><tr><Th>Candidate</Th><Th className="text-right">Recognized</Th><Th className="text-right">Received</Th><Th className="text-right">Outstanding</Th><Th>Status</Th><Th>Last Payment</Th></tr></thead>
          <tbody>{data.candidates.map((r) => (
            <tr key={r.candidate.id} onClick={() => navigate(`/candidates/${r.candidate.id}`)} className="hover:bg-slate-50 cursor-pointer">
              <Td className="font-medium">{r.candidate.name}</Td><Td className="text-right font-mono">{formatINR(r.recognized)}</Td>
              <Td className="text-right font-mono">{formatINR(r.received)}</Td><Td className="text-right font-mono font-semibold text-amber-600">{formatINR(r.outstanding)}</Td>
              <Td><Badge>{r.status}</Badge></Td><Td>{r.last_payment ? formatDate(r.last_payment) : "-"}</Td></tr>
          ))}</tbody>
        </TableWrap>
      )}
      <h3 className="font-heading font-semibold text-sm mb-2 mt-6">Agency / Payee Outstanding (Pending Advances)</h3>
      {data.payees.length === 0 ? <EmptyState title="No agency outstanding" /> : (
        <TableWrap>
          <thead><tr><Th>Payee</Th><Th className="text-right">Advance</Th><Th className="text-right">Adjusted</Th><Th className="text-right">Pending</Th></tr></thead>
          <tbody>{data.payees.map((r) => (
            <tr key={r.payee.id} onClick={() => navigate(`/payees/${r.payee.id}`)} className="hover:bg-slate-50 cursor-pointer">
              <Td className="font-medium">{r.payee.name}</Td><Td className="text-right font-mono">{formatINR(r.advance)}</Td>
              <Td className="text-right font-mono">{formatINR(r.adjusted)}</Td><Td className="text-right font-mono font-semibold text-violet-600">{formatINR(r.pending)}</Td></tr>
          ))}</tbody>
        </TableWrap>
      )}
    </div>
  );
}
